import React from 'react';
import { BomReasoningRow, TRIGGER_LABELS } from '../../../types/bomVersions';
import { Badge } from '../../ui';
import { changeParts, rowActor } from './reasoningRowView';

/**
 * The "Reasoning" sheet: read-only rows explaining every change up to the
 * viewed version — version, trigger, who, component, what changed (field:
 * before → after), and why. The "why" is real stored data (suggestion
 * reasoning, validation findings, what the trigger did); this sheet only
 * renders it.
 */
export interface ReasoningSheetProps {
  rows: BomReasoningRow[];
}

const GRID_TEMPLATE =
  '52px 84px 130px minmax(120px,0.9fr) minmax(140px,1fr) minmax(200px,1.4fr) minmax(220px,1.6fr)';

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const ActorCell: React.FC<{ row: BomReasoningRow }> = ({ row }) => {
  const actor = rowActor(row);
  if (actor.kind === 'robbie') {
    return (
      <span className="flex min-w-0 flex-col items-start gap-[2px]">
        <Badge tone="robbie">Robbie</Badge>
        <span className="truncate text-meta text-ink-muted" title={`approved by ${actor.approvedBy}`}>
          approved by {actor.approvedBy}
        </span>
      </span>
    );
  }
  return (
    <span className="truncate text-body-sm text-ink-secondary" title={actor.label}>
      {actor.label}
    </span>
  );
};

const ChangeCell: React.FC<{ row: BomReasoningRow }> = ({ row }) => {
  const parts = changeParts(row);
  if (!parts) return <span className="break-words text-body-sm text-ink-body">{row.change}</span>;
  return (
    <span className="break-words text-body-sm">
      {parts.field && <span className="text-ink-muted">{parts.field}: </span>}
      <span className={`text-ink-muted ${parts.before === '—' ? '' : 'line-through'}`}>{parts.before}</span>
      <span className="px-[4px] text-ink-muted" aria-label="changed to">→</span>
      <span className="text-ink">{parts.after}</span>
    </span>
  );
};

export const ReasoningSheet: React.FC<ReasoningSheetProps> = ({ rows }) => (
  <div className="h-full overflow-auto" role="table" aria-label="Reasoning sheet">
    <div className="min-w-[1000px]">
      <div
        role="row"
        className="sticky top-0 z-[1] grid gap-[10px] border-b border-line bg-canvas px-s5 py-[8px] text-table-header uppercase text-ink-muted"
        style={{ gridTemplateColumns: GRID_TEMPLATE }}
      >
        <span role="columnheader">Ver</span>
        <span role="columnheader">Date</span>
        <span role="columnheader">Trigger</span>
        <span role="columnheader">By</span>
        <span role="columnheader">Component</span>
        <span role="columnheader">Change</span>
        <span role="columnheader">Why</span>
      </div>

      {rows.length === 0 && (
        <div className="px-s6 py-s7 text-center text-body-sm text-ink-muted">
          No recorded changes up to this version yet.
        </div>
      )}

      {rows.map((row, index) => (
        <div
          key={index}
          role="row"
          className="grid gap-[10px] border-b border-line-divider px-s5 py-[9px] last:border-b-0 hover:bg-surface-hover"
          style={{ gridTemplateColumns: GRID_TEMPLATE }}
        >
          <span className="font-mono text-mono-data text-ink">v{row.version}</span>
          <span className="font-mono text-mono-data text-ink-secondary">{formatDate(row.date)}</span>
          <span className="text-body-sm text-ink-secondary">{TRIGGER_LABELS[row.trigger] || row.trigger}</span>
          <ActorCell row={row} />
          <span className="truncate text-body-md text-ink" title={row.componentName}>
            {row.componentName}
          </span>
          <ChangeCell row={row} />
          <span className="break-words text-body-sm text-ink-secondary">{row.why}</span>
        </div>
      ))}
    </div>
  </div>
);

export default ReasoningSheet;
